import { DEFAULT_REGION_BANK_HEADER_VALUE } from '@/constants'
import {
  brandSchema,
  categorySchema,
  categoryWithoutChildsSchema,
  countrySchema,
  nomenclatureGroupSchema,
  storeShortWithSupplierSchema,
  unitOfMeasurementSchema,
} from '@/schemas'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { StoresBySupplierType } from '@/types'
import { ignoreError } from '@/utils/api/errors/ignoreError'

import { filterApprovedCategoryTree } from '../../catalog/approvalStatus'
import { cleanFilterItems } from '../../catalog/cleanDescription'
import { sortStores } from '../../search/stores/sortStores'
import api from '../api'

export async function fetchBrands() {
  const response = await api.get('/brands')
  const validatedBrands = brandSchema.array().parse(response.data.brands)
  return cleanFilterItems(validatedBrands)
}

export async function fetchCountries() {
  const response = await api.get('/countries/all')
  const validatedCountries = countrySchema
    .array()
    .parse(response.data.countries)
  return validatedCountries
}

export async function fetchUnitsOfMeasurement() {
  const response = await api.get('/units-of-measurement/all')
  const validatedUnitsOfMeasurement = unitOfMeasurementSchema
    .array()
    .parse(response.data.unitsOfMeasurement)
  return validatedUnitsOfMeasurement
}

async function loadCategoriesTree() {
  const response = await api.get('/categories/root-trees')
  const validatedCategoriesTree = categorySchema
    .array()
    .parse(response.data.trees)
  return validatedCategoriesTree
}

let pendingCategoriesTree: ReturnType<typeof loadCategoriesTree> | null = null

export function fetchCategoriesTree() {
  if (!pendingCategoriesTree) {
    pendingCategoriesTree = loadCategoriesTree().finally(() => {
      pendingCategoriesTree = null
    })
  }
  return pendingCategoriesTree
}

export async function fetchApprovedCategoriesTree() {
  const tree = await fetchCategoriesTree()
  return filterApprovedCategoryTree(tree)
}

export async function fetchCategories() {
  const response = await api.get('/categories/all')
  const validatedCategories = categoryWithoutChildsSchema
    .array()
    .parse(response.data.categories)
  return validatedCategories
}

export async function fetchNomenclatureGroupsTree() {
  const response = await api.get('/main/nomenclature-groups/root-trees')
  const validatedNomenclatureGroupsTree = nomenclatureGroupSchema
    .array()
    .parse(response.data.trees)
  return validatedNomenclatureGroupsTree
}

export async function fetchStoresWithSuppliers() {
  const response = await api.get('/suppliers/stores')

  const storesBySuppliers: StoresBySupplierType = response.data.storesBySupplier

  const formattedStoresBySuppliers = Object.keys(storesBySuppliers)
    .map((supplier) =>
      storesBySuppliers[supplier].map((store) => ({
        ...store,
        supplierId: supplier,
      }))
    )
    .flat(2)

  const validatedStores = storeShortWithSupplierSchema
    .array()
    .parse(formattedStoresBySuppliers)
  return sortStores(validatedStores)
}

export async function fetchCart(params: {
  setProductsQuantity?: (total: number) => void
}) {
  const { setProductsQuantity } = params
  const { currentClient, userOrganisation } = useUserInfoStore.getState()

  let clientId: string | null = null

  if (currentClient?.id) {
    clientId = currentClient.id
  } else if (userOrganisation?.id) {
    clientId = userOrganisation.id
  }

  const response = await api.get('/cart', {
    params: {
      client: clientId,
    },
    headers: {
      'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE,
    },
  })

  try {
    const total = response?.data?.cart?.cartItems?.totalElements
    if (typeof total === 'number' && setProductsQuantity) {
      setProductsQuantity(total)
    }
  } catch {
    ignoreError()
  }

  return response.data
}
